// Port of ecommerce/shelf/items_list.coffee calcItemsListProps — the "Shop Metrics" table.
// The nine shared cart/attention metrics live in sh/lib/shelf-metrics-table (the
// shelf-test question renders the same set); ecommerce adds the banner and Subscribe & Save
// sections below them.

import { formatWeeks } from '@aytm/app/stats/page/question-views/ec/lib/formatWeeks'
import {
  buildShelfMetricsCols,
  buildShelfMetricsRows,
  headerRow,
  metricRow,
  percent,
  round2
} from '@aytm/app/stats/page/question-views/sh/lib/shelf-metrics-table'

const buildBannerRows = ({ banners, nextPos, isLoading }) => [
  headerRow('Banners', nextPos),
  metricRow('Total banner impressions', banners.totalImpressions || 0, nextPos + 1, isLoading),
  metricRow('Total banner clicks', banners.totalClicks || 0, nextPos + 2, isLoading),
  metricRow('Banner CTR', percent(banners.ctr), nextPos + 3, isLoading)
]

const buildSubscribeRows = ({ sub, nextPos, isLoading, showPrice, priceMask }) => {
  const spend = showPrice ? priceMask.replace('{{price}}', round2(sub.totalSubscribeSpend || 0)) : 'N/A'

  const rows = [
    headerRow('Subscribe & Save', nextPos),
    metricRow('Subscribe rate', percent(sub.subscribeRate), nextPos + 1, isLoading),
    metricRow('Total subscriptions', sub.totalSubscriptions || 0, nextPos + 2, isLoading),
    metricRow('Total subscribe spend', spend, nextPos + 3, isLoading),
    metricRow('Subscribe reach rate', percent(sub.subscribeReachRate), nextPos + 4, isLoading),
    metricRow('Average time to first toggle', `${round2(sub.averageTimeToFirstToggle || 0)}s`, nextPos + 5, isLoading),
    metricRow('Consideration rate', percent(sub.considerationRate), nextPos + 6, isLoading),
    metricRow('Churn rate', percent(sub.churnRate), nextPos + 7, isLoading),
    metricRow('Reversal rate', percent(sub.reversalRate), nextPos + 8, isLoading),
    metricRow('Total reversals', sub.totalReversals || 0, nextPos + 9, isLoading),
    metricRow('Total purchase only', sub.totalPurchaseOnly || 0, nextPos + 10, isLoading),
    metricRow('Total toggles', sub.totalToggles || 0, nextPos + 11, isLoading)
  ]

  if (!sub.frequencyDistribution?.length) return rows

  // Only the three most-chosen intervals get their own line; the remainder is collapsed so
  // the percentages still read as a whole.
  const top3 = sub.frequencyDistribution.slice(0, 3)
  const topSum = top3.reduce((s, f) => s + Math.round(f.pct * 10) / 10, 0)
  const otherPct = Math.round((100 - topSum) * 10) / 10

  let pos = nextPos + rows.length
  top3.forEach(freq => {
    rows.push(
      metricRow(`Frequency: every ${formatWeeks(freq.weeks, true)}`, `${freq.pct.toFixed(1)}%`, pos++, isLoading)
    )
  })

  if (otherPct > 0) {
    rows.push(metricRow('Frequency: other', `${otherPct.toFixed(1)}%`, pos++, isLoading))
  }

  return rows
}

export const calcShelfTableProps = ({ answer, viewMode, format, priceTemplate, shelfPreviewData }) => {
  const isLoading = 'loading' === answer
  const values = isLoading || answer == undefined ? {} : answer

  const priceMask = priceTemplate || '{{price}}'
  const showPrice = !shelfPreviewData?.hide_price
  const hideTotal = true === format?.hideTotal

  const cols = buildShelfMetricsCols({ values, viewMode, hideTotal })
  const rows = buildShelfMetricsRows({ values, isLoading, showPrice, priceMask })

  if (values.banners) {
    rows.push(...buildBannerRows({ banners: values.banners, nextPos: rows.length + 1, isLoading }))
  }

  if (values.subscribe) {
    rows.push(
      ...buildSubscribeRows({ sub: values.subscribe, nextPos: rows.length + 1, isLoading, showPrice, priceMask })
    )
  }

  return { cols, rows }
}
